// Offline mixdown: rebuilds the mixer graph in an OfflineAudioContext and
// renders the project — or any range of it — to AudioBuffers. Long timelines are
// rendered in chunks (renderChunks) so a 2-hour export never needs one multi-GB
// buffer: each chunk starts a few seconds early ("pre-roll") so reverb tails,
// compressors and fades are already in the right state at the chunk boundary,
// then the pre-roll is discarded and chunks join sample-exactly.
import { Project, Track, clipSpeed } from "../state/model";
import { audioPlans } from "../state/transitions";
import { scheduleFades } from "../audio/engine";
import { createEffectNode, ensureDenoiseWorklet, ensureDeverbWorklet, chainLatency } from "../audio/effects";

export interface RenderOpts {
  sampleRate?: number;
  /** Render these tracks instead of the audible mix (stems). */
  tracks?: Track[];
  /** Include the master bus FX + volume (default true; stems are pre-master). */
  master?: boolean;
}

/** End of the last active, enabled clip (s). */
export function projectAudioEnd(project: Project): number {
  let end = 0;
  for (const t of project.tracks)
    for (const c of t.clips) if (c.takeActive !== false && !c.disabled) end = Math.max(end, c.start + c.duration);
  return end;
}

/** Render timeline frames [startFrame, endFrame) at `sampleRate` (stereo). */
export async function renderFrames(project: Project, buffers: Map<string, AudioBuffer>, startFrame: number, endFrame: number, opts: RenderOpts & { prerollFrames?: number } = {}): Promise<AudioBuffer> {
  const sampleRate = opts.sampleRate ?? 48000;
  const withMaster = opts.master !== false;
  const outLen = Math.max(1, endFrame - startFrame);
  const preFrames = Math.min(startFrame, Math.max(0, opts.prerollFrames ?? 0));
  const winStartFrame = startFrame - preFrames;
  const winStart = winStartFrame / sampleRate, end = endFrame / sampleRate;

  const anySolo = project.tracks.some((t) => t.solo);
  const tracks = opts.tracks ?? project.tracks.filter((t) => t.kind !== "video" && !t.mute && (!anySolo || t.solo));

  // Plugin-delay compensation: delay shorter chains so every track aligns, render
  // extra head room, then trim it so the mix lines up with the timeline.
  const tmpCtx = new OfflineAudioContext(1, 1, sampleRate);
  const denoiseReady = await ensureDenoiseWorklet(tmpCtx);
  const deverbReady = await ensureDeverbWorklet(tmpCtx);
  const trackLat = (t: Track) => chainLatency(t.fx, denoiseReady, deverbReady);
  const maxTrackLat = tracks.reduce((m, t) => Math.max(m, trackLat(t)), 0);
  const masterLat = withMaster ? chainLatency(project.masterFx, denoiseReady, deverbReady) : 0;
  const lead = maxTrackLat + masterLat;

  const ctx = new OfflineAudioContext(2, preFrames + outLen + lead, sampleRate);
  await ensureDenoiseWorklet(ctx);
  await ensureDeverbWorklet(ctx);

  const masterIn = ctx.createGain();
  if (withMaster) {
    const masterFader = ctx.createGain();
    masterFader.gain.value = project.masterVolume;
    let mCursor: AudioNode = masterIn;
    for (const fx of project.masterFx) {
      if (fx.kind === "wam" || !fx.enabled) continue;
      const n = createEffectNode(ctx, fx);
      mCursor.connect(n.input); mCursor = n.output;
    }
    mCursor.connect(masterFader);
    masterFader.connect(ctx.destination);
  } else masterIn.connect(ctx.destination);

  for (const track of tracks) {
    const fxInput = ctx.createGain();
    const panner = ctx.createStereoPanner();
    panner.pan.value = track.pan;
    const fader = ctx.createGain();
    fader.gain.value = track.volume;
    let cursor: AudioNode = fxInput;
    for (const fx of track.fx) {
      if (fx.kind === "wam" || !fx.enabled) continue;
      const n = createEffectNode(ctx, fx);
      cursor.connect(n.input); cursor = n.output;
    }
    cursor.connect(panner); panner.connect(fader);
    const comp = (maxTrackLat - trackLat(track)) / sampleRate;
    if (comp > 0) { const dl = ctx.createDelay(comp + 1); dl.delayTime.value = comp; fader.connect(dl); dl.connect(masterIn); }
    else fader.connect(masterIn);

    const plans = audioPlans(track.clips, (c) => buffers.get(c.assetId)?.duration ?? Infinity);
    for (const clip of track.clips) {
      if (clip.takeActive === false || clip.disabled) continue;
      const buffer = buffers.get(clip.assetId);
      if (!buffer) continue;
      const plan = plans.get(clip.id)!;
      if (plan.end <= winStart || plan.start >= end) continue; // outside this window
      const speed = clipSpeed(clip);
      const from = Math.max(plan.start, winStart);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      if (speed !== 1) src.playbackRate.value = speed;
      const cg = ctx.createGain();
      const at = from - winStart;
      scheduleFades(cg.gain, clip.gain, plan, from, at);
      src.connect(cg); cg.connect(fxInput);
      src.start(at, plan.offset + (from - plan.start) * speed, (plan.end - from) * speed); // duration in buffer time
    }
  }

  const rendered = await ctx.startRendering();
  const skip = lead + preFrames;
  if (skip === 0 && rendered.length === outLen) return rendered;
  const out = new AudioBuffer({ numberOfChannels: 2, length: outLen, sampleRate });
  for (let ch = 0; ch < 2; ch++) out.copyToChannel(rendered.getChannelData(ch).subarray(skip, skip + outLen), ch);
  return out;
}

/** Render [start, end) seconds in chunks, calling `onChunk` in order. Chunks are
 *  contiguous to the sample. Returns the total frame count. */
export async function renderChunks(
  project: Project, buffers: Map<string, AudioBuffer>,
  opts: RenderOpts & { start: number; end: number; chunkSeconds?: number; prerollSeconds?: number; onProgress?: (frac: number) => void },
  onChunk: (buf: AudioBuffer, index: number) => Promise<void> | void,
): Promise<number> {
  const sr = opts.sampleRate ?? 48000;
  const f0 = Math.round(opts.start * sr), f1 = Math.max(f0 + 1, Math.round(opts.end * sr));
  const chunk = Math.round((opts.chunkSeconds ?? 120) * sr);
  const pre = Math.round((opts.prerollSeconds ?? 4) * sr);
  let i = 0;
  for (let f = f0; f < f1; f += chunk, i++) {
    const buf = await renderFrames(project, buffers, f, Math.min(f1, f + chunk), { ...opts, sampleRate: sr, prerollFrames: pre });
    await onChunk(buf, i);
    opts.onProgress?.(Math.min(1, (f + chunk - f0) / (f1 - f0)));
  }
  return f1 - f0;
}

/** Whole-project mixdown in one buffer (short projects / previews). Includes a
 *  tail so reverbs ring out. For long exports use renderChunks. */
export async function renderMixdown(
  project: Project,
  buffers: Map<string, AudioBuffer>,
  opts: { sampleRate?: number; tailSeconds?: number } = {},
): Promise<AudioBuffer> {
  const sr = opts.sampleRate ?? 48000;
  const length = Math.max(1, projectAudioEnd(project) + (opts.tailSeconds ?? 2));
  return renderFrames(project, buffers, 0, Math.ceil(length * sr), { sampleRate: sr });
}
